import React, { useMemo } from 'react';
import styles from './Alert.module.css'
import commonStyles from '../../styles/common.module.css'
import { processAlertComment, processDuration } from "../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { deselectAlert, selectAlert } from "../../store/reducers/alertReducer";
import { setModalError, openModal } from "../../store/reducers/modalReducer";
import { sha256 } from "js-sha256";
import AlertService from "../../services/AlertService";
import { HISTORY_DISPLAY, MAIN_DISPLAY, SILENCED_DISPLAY } from "../../store/actions/DISPLAY_ACTIONS";
import { Link } from "react-router-dom";
import { RECHECKABLE_INPUT_PLUGIN } from "../../utils/vars";

const Alert = ({
    alert,
    isSilenceWindowOpen,
    silenceDuration,
    silenceDurationError,
    silenceDurationHint,
    silenceComment,
    onToggleSilenceWindow,
    onSilenceDurationChange,
    onSilenceCommentChange,
    onSubmitSilenceAlert,
}) => {
    const dispatch = useDispatch()
    const isInspectMode = useSelector(state => state.setHeaderMenuItemValue.inspectMode)
    const userEmail = useSelector(state => state.authReducer.user.userEmail)
    const displayMode = useSelector(state => state.setDisplay.display)
    const recheckStatus = useSelector(state => {
        const matchedAlert = state.webSocket.recheckAlerts.find(entry => entry.alert_id === alert.alert_id)
        if (matchedAlert) {
            return matchedAlert.recheck_status === "poo" ? "poo" : "other"
        }
        return null
    })
    const user = useSelector(state => state.authReducer.user)
    const isSelectedAlert = useSelector(state => state.setAlertReducer.selectedAlerts.includes(alert.alert_id))
    const historyDate = useSelector(state => state.setAlertReducer.historyDate)

    const isRecheckableAlert = RECHECKABLE_INPUT_PLUGIN.includes(alert.plugin_id)
    const isEnabledSilenceButton = Boolean(silenceComment && silenceComment.length > 4 && !silenceDurationError)
    const isRefresh = recheckStatus === "other"
    const failedRecheck = recheckStatus === "poo"
    const fixInstruction = alert?.customFields?.fixInstructions || ''
    const alertComment = useMemo(() => {
        const processedString = (alert.comment || "")
            .split(/\s+/)
            .map(word => processAlertComment(word, user.usersCommentReplaceRules))
        return (
            <>
                {processedString.map((element, index) => (
                    <React.Fragment key={index}>{element} </React.Fragment>
                ))}
            </>
        )
    }, [alert.comment, user.usersCommentReplaceRules])

    const handleCheckboxChange = () => {
        if (isSelectedAlert) {
            dispatch(deselectAlert(alert.alert_id))
        } else {
            dispatch(selectAlert(alert.alert_id))
        }
    }


    const ackButtonHint = alert.responsibleUser ? "unhandle alert" : "handle alert";
    const ackedAlert = {"alertId": alert.alert_id}
    const onAckHandle = async (ackedAlert) => {
        try {
            if (alert.responsibleUser === userEmail){
                 await AlertService.unack([ackedAlert])
            } else {
                 await AlertService.ack([ackedAlert])
            }
        }
        catch (e) {

            dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
        }
    }

    const onRecheckHandle = async () => {
        try {
            await AlertService.refreshAlerts([["recheck", alert.alert_id]])
        }
        catch (e) {
            dispatch(openModal())
            dispatch(setModalError(e.response.data.name))
        }
    }


    const handleKeyDown = (e) => {
        if (e.keyCode === 13) {
            e.preventDefault()
            if (isEnabledSilenceButton) {
                onSubmitSilenceAlert(alert)
            }
        }
    }

    let alertBackground
    let fontColor
    if(alert.silenced){
        fontColor = '#808080'
    } else {
        fontColor = 'black'
    }
    switch (alert.severity.toUpperCase()){
        case 'WARNING':
            alertBackground = '#FEFFC1'
            break
        case 'CRITICAL':
            alertBackground = '#FFBBBA'
            break
        case 'EMERGENCY':
            alertBackground = '#f67d7a'
            break
        case 'INFO':
            alertBackground = '#BED8FF'
            break
        case 'UNKNOWN':
            alertBackground = '#ffda9f'
            break
        default:
            alertBackground = '#bab8b8'
    }


    const handleSilenceButton = () => {
        onToggleSilenceWindow(alert.alert_id)
    }



    return (
        <div className={`${!isInspectMode ? styles.alertBody : styles.alertBody_small}`}
             style={{backgroundColor: alertBackground, color: fontColor}}>
            <div
                className={`${!isInspectMode ? styles.projectName : styles.projectName_small}`}>{alert.project[0].toUpperCase()}</div>
            <div className={`${!isInspectMode ? styles.host : styles.host_small}`}>
                <label>
                    <input
                        className={`${styles.selectCheckBox} ${!isInspectMode ? styles.selectCheckBox_scaled : null}`}
                    type="checkbox"
                        onChange={handleCheckboxChange}
                        checked={isSelectedAlert}
                    />
                </label>
                <p className={styles.textFields}>{alert.host}</p>
            </div>
            <div className={`${!isInspectMode ? styles.severity : styles.severity_small}`}>
                <p className={styles.textFields}>{alert.severity.toUpperCase()}</p>
            </div>
            <button
                className={`${!isInspectMode ? styles.responsibleUser : styles.responsibleUser_small} ${commonStyles.buttonHint}`}
                style={{
                    backgroundImage: `url(${alert.responsibleUser
                        ? `https://gravatar.com/avatar/${sha256(alert.responsibleUser)}?s=150`
                        : process.env.PUBLIC_URL + "/images/user.svg"})`
                }}
                data-tooltip={ackButtonHint}
                onClick={e => {
                    e.preventDefault()
                    onAckHandle(ackedAlert)
                }}
                disabled={displayMode === HISTORY_DISPLAY || displayMode === SILENCED_DISPLAY}
            />
            <div className={`${!isInspectMode ? styles.alertName : styles.alertName_small}`}>

                <Link to={`?alert_id=${alert.alert_id}`}
                      className={`${styles.alertNameLink}`}>{alert.alertName}
                </Link>

            </div>
            <div
                className={`${!isInspectMode ? styles.alertTime : styles.alertTime_small}`}>{processDuration(alert.fired, displayMode === HISTORY_DISPLAY ? historyDate : undefined)}
            </div>
            <div className={`${!isInspectMode ? styles.message : styles.message_small}`}>
                <div className={`
                ${styles.descriptionSection} 
                ${displayMode === MAIN_DISPLAY ? styles.descriptionSectionFull : null}
                ${isInspectMode ? styles.descriptionSection_small : null}
                `}
                >
                    <p className={`${styles.textFields} ${displayMode === MAIN_DISPLAY && alert.silenced === true ? isInspectMode ? styles.textFieldsShare_small : styles.textFieldsShare : null}`}>{alert.msg}</p>
                    {
                        displayMode === MAIN_DISPLAY && alert.silenced === true
                            ?
                            <p className={`${styles.textFields} ${isInspectMode ? styles.textFieldsShare_small : styles.textFieldsShare} `}>
                                <>
                                    <span>Comment: </span>
                                    {alertComment || alert.comment}
                                </>
                            </p>
                            :
                            null
                    }
                </div>
                {
                    displayMode === SILENCED_DISPLAY ?
                        <div className={`${styles.comment} ${isInspectMode ? styles.comment_small : null}`}>
                            <p className={styles.textFields}>
                                <>
                                    <span>Comment: </span>
                                    {alertComment || alert.comment}
                                </>
                            </p>
                        </div>
                        : null
                }
            </div>

            <div className={`${styles.functionButtonsBlock} ${isInspectMode ? styles.functionButtonsBlock_small : null}`}>
                {
                    fixInstruction
                        ?
                        <a className={`${!isInspectMode ? styles.controlButton : styles.controlButton_small} ${styles.fixInstructions} ${commonStyles.buttonHint}`}
                           href={fixInstruction} target="_blank" rel="noopener noreferrer">
                        </a>
                        : <div className={`${!isInspectMode ? styles.controlButton : styles.controlButton_small}`}/>
                }
                <div
                    className={`${styles.silenceWindow} ${isSilenceWindowOpen ? styles.silenceWindowActive : styles.silenceWindowDisabled} ${!isInspectMode ? null : styles.silenceWindow_small}`}>
                    <input type="text"
                           className={`${styles.silenceDuration} ${!isInspectMode ? null : styles.silenceDuration_small} ${silenceDurationError ? styles.silenceInputInvalid : ''}`}
                           placeholder="30m, 2h, 1 day"
                           id={`duration_${alert.alert_id}`}
                           value={silenceDuration}
                           title={silenceDurationHint}
                           onKeyDown={handleKeyDown}
                           onChange={e => onSilenceDurationChange(e.target.value)}
                    />
                    <input type="text"
                           className={`${styles.silenceComment} ${!isInspectMode ? null : styles.silenceComment_small}`}
                           placeholder="comment"
                           id={`comment_${alert.alert_id}`}
                           value={silenceComment}
                           onKeyDown={handleKeyDown}
                           onChange={e => onSilenceCommentChange(e.target.value)}
                    />
                    <button disabled={!isEnabledSilenceButton}
                            className={`${isEnabledSilenceButton ? styles.silenceButtonEnabled : styles.silenceButtonDisabled} ${styles.silenceButton} ${!isInspectMode ? null : styles.silenceButton_small}`}
                            onClick={(e) => {
                                e.preventDefault()
                                onSubmitSilenceAlert(alert)
                            }}
                    >
                        Silence
                    </button>
                </div>
                <button className={`${!isInspectMode ? styles.controlButton : styles.controlButton_small} 
                ${isRecheckableAlert
                    ? `${failedRecheck ? styles.failedRefresh : styles.refresh} ${isRefresh ? commonStyles.rotatedIcon : commonStyles.buttonHint}`
                    : ''}`}
                        data-tooltip="recheck alert"
                        onClick={event => {
                            event.preventDefault()
                            onRecheckHandle()
                        }}
                        disabled={isRefresh || !isRecheckableAlert}
                />
                {displayMode === MAIN_DISPLAY
                    ? <button
                        className={`${!isInspectMode ? styles.controlButton : styles.controlButton_small} ${styles.silence} ${commonStyles.buttonHint}`}
                        data-tooltip="silence alert"
                        onClick={e => {
                            e.preventDefault()
                            handleSilenceButton()
                        }}
                    />
                    : null
                }
            </div>
        </div>
    );
};

const areAlertPropsEqual = (prevProps, nextProps) => {
    return prevProps.alert === nextProps.alert
        && prevProps.isSilenceWindowOpen === nextProps.isSilenceWindowOpen
        && prevProps.silenceDuration === nextProps.silenceDuration
        && prevProps.silenceDurationError === nextProps.silenceDurationError
        && prevProps.silenceDurationHint === nextProps.silenceDurationHint
        && prevProps.silenceComment === nextProps.silenceComment
}

export default React.memo(Alert, areAlertPropsEqual);
